import {
  Frontiers,
  LoroDoc,
  LoroMap,
  LoroMovableList,
  UndoManager,
  VersionVector,
} from "loro-crdt";
import {
  BaseStateShape,
  stateShapeSchema,
  isDerivedShapeInput,
  MapShapeInput,
  StateShape,
  BaseTentativeShape,
  TentativeShapeType,
  oppositeCornersToRectangle,
  positionSchema,
} from "./lib/shape";
import {
  DataType,
  DerivedDataType,
  getBaseDataType,
  getDerivedDataTypes,
  isBaseDataType,
  isCustomOrderDataType,
  isDerivedDataType,
  PointCloudDataType,
} from "./lib/dataType";
import { idLib } from "./lib";
import { positionLib } from "./lib/position";
import { rectangleLib } from "./lib/rectangle";
import invariant from "tiny-invariant";
import { Corner } from "./lib/rectangle";
import { stateSchema, tempStateSchema, UserSettings } from "./lib/state";
import { objectAdd, objectAssign } from "@larki/shared/object";
import { Position } from "geojson";
import * as turf from "@turf/turf";

type DocPolygon = BaseStateShape & {
  type: "polygon";
  exterior: LoroMovableList<string>;
  holes?: LoroMovableList<LoroMovableList<string>>;
};
type DocRectangle = BaseStateShape & {
  type: "rectangle";
  exterior: LoroMap<{
    [Corner.sw]: string;
    [Corner.se]: string;
    [Corner.ne]: string;
    [Corner.nw]: string;
  }>;
  holes?: LoroMovableList<LoroMovableList<string>>;
};
type DocPath = BaseStateShape & {
  type: "path";
  vertices: LoroMovableList<string>;
};
type DocExtents = BaseStateShape & {
  type: "extents";
  extents: LoroMovableList<string>;
};
type DocShape = DocPolygon | DocRectangle | DocPath | DocExtents;
export type DocDerivedShape = DocShape & {
  dataType: DerivedDataType;
  baseShapeId: string;
};
export type Doc2dPlanOnMapShape = DocShape & {
  dataType: "_2d_plan_on_map";
  planS3Url: string;
  assetId?: string;
};

export type MapShape = StateShape & {
  hidden: boolean;
};
type DerivedMapShape = MapShape & {
  dataType: DerivedDataType;
  baseShapeId: string;
};

type DocTentativePolygon = BaseTentativeShape & {
  type: "polygon";
  exterior: LoroMovableList<string>;
};
type DocTentativeRectangle = BaseTentativeShape & {
  type: "rectangle";
  initial: string;
};
type DocTentativePath = BaseTentativeShape & {
  type: "path";
  vertices: LoroMovableList<string>;
};
type DocTentativeBoolean = BaseTentativeShape & {
  type: "boolean";
  parent_index: number;
  vertices: LoroMovableList<string>;
};
type DocTentativeShape =
  | DocTentativePolygon
  | DocTentativeRectangle
  | DocTentativePath
  | DocTentativeBoolean;
type DocCustomOrder = {
  id: string;
  name: string;
  description?: string;
  color: [number, number, number];
  price?: number | "TBC";
};
export type DocState = {
  shapes: LoroMovableList<LoroMap<DocShape>>;
  custom_orders: LoroMovableList<LoroMap<DocCustomOrder>>;
  contributors: LoroMap<Record<`${number}`, number | undefined>>;
};
type ClientState = {
  drag_vertex_index?: number;
  drag_shape_index?: number;
  drag_hole_index?: number;
  drag_corner?: Corner;
  user_settings?: LoroMap<UserSettings>;
};
export type DocTmpState = {
  hidden_map: LoroMap<Record<string, boolean | undefined>>;
  tentative_shapes: LoroMap<
    Record<string, LoroMap<DocTentativeShape> | undefined>
  >;
  clients: LoroMap<Record<string, LoroMap<ClientState> | undefined>>;
};

type MpDocOptions = {
  changeMergeInterval: number;
  peerId?: number | `${number}`;
};

export class MpDoc {
  #state: LoroDoc<DocState>;
  #tmpState: LoroDoc<DocTmpState>;
  #opts: MpDocOptions;

  constructor(
    state: LoroDoc<DocState>,
    tmpState: LoroDoc<DocTmpState>,
    opts?: MpDocOptions,
  ) {
    this.#opts = {
      changeMergeInterval: opts?.changeMergeInterval ?? 300, // 5 mins
      peerId: opts?.peerId,
    };
    if (this.#opts.changeMergeInterval <= 0) {
      throw new Error(`changeMergeInterval > 0 required`);
    }
    // always reapply config, why?
    // > Note that these settings do not persist in exported Updates or Snapshots.
    // > Thus, if custom configurations are required, they must be reapplied upon each initialization of LoroDoc
    if (this.#opts.peerId) {
      state.setPeerId(this.#opts.peerId);
    }
    state.setRecordTimestamp(true);
    state.setChangeMergeInterval(this.#opts.changeMergeInterval);
    this.#state = state;
    this.#tmpState = tmpState;
  }

  static empty(opts?: MpDocOptions) {
    return new MpDoc(new LoroDoc<DocState>(), new LoroDoc<DocTmpState>(), opts);
  }

  static fromSnapshot({
    state,
    tmpState,
    opts,
  }: {
    state: Uint8Array;
    tmpState?: Uint8Array;
    opts?: MpDocOptions;
  }) {
    return new MpDoc(
      LoroDoc.fromSnapshot(state) as LoroDoc<DocState>,
      tmpState
        ? (LoroDoc.fromSnapshot(tmpState) as LoroDoc<DocTmpState>)
        : new LoroDoc(),
      opts,
    );
  }

  import({
    stateChanges,
    tmpStateChanges,
  }: // contributorId,
  {
    stateChanges?: Uint8Array;
    tmpStateChanges?: Uint8Array;
    // contributorId?: number;
  }) {
    let stateChanged = false;
    if (stateChanges) {
      const v0 = this.#state.frontiers();
      this.#state.import(stateChanges);
      const v1 = this.#state.frontiers();
      stateChanged = this.#state.cmpFrontiers(v1, v0) !== 0;
      // if (stateChanged && contributorId) {
      //   this.contribute(contributorId, Date.now());
      // }
    }
    let tmpStateChanged = false;
    if (tmpStateChanges) {
      const v0 = this.#tmpState.frontiers();
      this.#tmpState.import(tmpStateChanges);
      const v1 = this.#tmpState.frontiers();
      tmpStateChanged = this.#tmpState.cmpFrontiers(v1, v0) !== 0;
    }
    return {
      stateChanged,
      tmpStateChanged,
    };
  }

  export({
    stateFromVersion,
    tmpStateFromVersion,
  }: {
    stateFromVersion?: VersionVector;
    tmpStateFromVersion?: VersionVector;
  }) {
    const state = this.#state.export({
      mode: "update",
      from: stateFromVersion,
    });
    const tmpState = this.#tmpState.export({
      mode: "update",
      from: tmpStateFromVersion,
    });
    return {
      state,
      tmpState,
      stateVersion: this.#state.version(),
      tmpStateVersion: this.#tmpState.version(),
    };
  }

  exportSnapshot() {
    return {
      state: this.#state.export({ mode: "snapshot" }),
      tmpState: this.#tmpState.export({ mode: "snapshot" }),
    };
  }

  // State-only: callers that persist just the main snapshot shouldn't pay to
  // serialize tmpState, which exportSnapshot() also computes.
  exportStateSnapshot() {
    return this.#state.export({ mode: "snapshot" });
  }

  cmpFrontiers(frontiers: Frontiers) {
    return this.#state.cmpWithFrontiers(frontiers);
  }

  commit() {
    this.#state.commit();
  }

  getAllChanges() {
    return this.#state.getAllChanges();
  }

  forkAt(frontiers: Frontiers) {
    return this.#state.forkAt(frontiers) as LoroDoc<DocState>;
  }

  // forkAt hits a Rust unwrap on FrontiersNotFound when `frontiers` reference
  // ops absent from the op log (e.g. after a snapshot rebuild with fresh peer
  // IDs). loro's panic hook dumps a scary stack to stderr before the boundary
  // re-throws it as a JS Error("unreachable"). frontiersToVV answers the same
  // "are these frontiers resolvable?" question via a plain Result — no panic
  // dump — so probe with it first and let callers catch a clean throw.
  forkAtSafe(frontiers: Frontiers) {
    this.#state.frontiersToVV(frontiers);
    return this.#state.forkAt(frontiers) as LoroDoc<DocState>;
  }

  // Contributors from the live state, without forking. The fallback path in
  // getMap2d must not fork: forkAt(currentFrontiers) throws when the loaded
  // doc's own frontiers are unresolvable, which is exactly when the fallback
  // is needed. Reading the container directly can never hit FrontiersNotFound.
  getLiveContributors() {
    return this.#state.getMap("contributors");
  }

  getVersionHistory() {
    const changes = this.#state.getAllChanges();
    const versions: {
      frontiers: Frontiers;
      contributors: number[];
      timestamp: number;
    }[] = [];

    let minT = -Infinity;
    while (true) {
      const candidates = [...changes.values()].flatMap(
        (c) => c.map((c) => c.timestamp).filter((t) => t !== 0 && t >= minT), // t=0 can happen if `setRecordTimestamp` was false, so we ignore that case
      );

      if (!candidates.length) break;

      const t0 = Math.min(...candidates);

      const t1 = t0 + this.#opts.changeMergeInterval;

      const frontiers: Frontiers = [];
      for (const [peer, peerChanges] of changes.entries()) {
        let counter = -1;
        for (const change of peerChanges) {
          if (change.timestamp < t1) {
            counter = Math.max(counter, change.counter + change.length - 1);
          }
        }
        if (counter > -1) {
          frontiers.push({ counter, peer });
        }
      }

      if (frontiers.length > 0) {
        const fork = this.forkAtSafe(frontiers);

        // get contributors
        const contributorsContainer = fork.getMap("contributors");
        const contributors: number[] = [];
        for (const _key of contributorsContainer.keys()) {
          const key: `${number}` = _key;
          const timestamp = contributorsContainer.get(key) as number;
          if (timestamp >= t0) {
            // get number in key
            contributors.push(Number(key));
          }
        }

        versions.push({ frontiers, contributors, timestamp: t0 });
      }

      minT = t1;
    }

    return versions.reverse();
  }

  frontiers() {
    return this.#state.frontiers();
  }

  getStateVersion() {
    return this.#state.version();
  }
  getTmpStateVersion() {
    return this.#tmpState.version();
  }

  checkout(frontiers: Frontiers) {
    this.#state.checkout(frontiers);
  }

  /**
   * CLIENT-SIDE only: this is for PREVIEWING a previous version
   */
  revert(frontiers: Frontiers) {
    this.#state.checkoutToLatest();
    this.#state.revertTo(frontiers);
  }

  /**
   * Makes a previous version the current version permanently
   */
  restore(frontiers: Frontiers) {
    this.#state.checkoutToLatest();
    const from = this.#state.frontiers();
    const diff = this.#state.diff(from, frontiers);
    this.#state.applyDiff(diff);
  }

  checkoutToLatest() {
    // same as `.attach()`
    this.#state.checkoutToLatest();
  }

  createUndoManager() {
    return new UndoManager(this.#state, {
      mergeInterval: 0,
    });
  }

  TMPstateToJson() {
    return this.#state.toJSON();
  }

  toJson() {
    const stateJson = stateSchema.parse(this.#state.toJSON());
    const tempStateJson = tempStateSchema.parse(this.#tmpState.toJSON());
    return {
      ...objectAssign(stateJson, "shapes", (shapes) =>
        shapes?.map(
          (shape) =>
            objectAdd(
              shape,
              "hidden",
              tempStateJson.hidden_map?.[shape.id] ?? false,
            ) satisfies MapShape,
        ),
      ),
      ...tempStateJson,
    };
  }

  passing() {
    try {
      this.toJson();
    } catch (e) {
      console.error(this.#tmpState.toJSON());
      throw e;
    }
  }

  #shapeToJson(shape: LoroMap<DocShape>) {
    const hiddenMap = this.#tmpState.getMap("hidden_map");
    const shapeJson = stateShapeSchema.parse(shape.toJSON());
    return {
      ...shapeJson,
      hidden: hiddenMap.get(shapeJson.id) ?? false,
    };
  }

  *#getShapes() {
    const shapes = this.#state.getMovableList("shapes");
    for (let i = 0; i < shapes.length; i++) {
      yield shapes.get(i);
    }
  }

  getShapes(...ids: string[]) {
    return [...this.#getShapes()]
      .filter((s) => !ids?.length || ids.includes(s.get("id")))
      .map(this.#shapeToJson.bind(this));
  }

  #getShapeContainerByIndex(index: number) {
    const shapes = this.#state.getMovableList("shapes");
    return shapes.get(index);
  }

  #getShapeContainer(id: string): LoroMap<DocShape> | null {
    for (const shape of this.#getShapes()) {
      if (shape.get("id") === id) {
        return shape;
      }
    }
    return null;
  }

  getShapeIndexById(id: string) {
    for (let i = 0; i < this.numShapes(); i++) {
      const shape = this.#getShapeContainerByIndex(i);
      if (shape?.get("id") === id) {
        return i;
      }
    }
    return null;
  }

  numShapes() {
    return this.#state.getMovableList("shapes").length;
  }

  getShape(id: string): MapShape | null {
    const shape = this.#getShapeContainer(id);
    if (!shape) return null;
    return this.#shapeToJson(shape);
  }

  getShapeByIndex(index: number): MapShape | null {
    const shape = this.#getShapeContainerByIndex(index);
    if (!shape) return null;
    return this.#shapeToJson(shape);
  }

  hideShape(id: string, hide: boolean) {
    const hiddenMap = this.#tmpState.getMap("hidden_map");
    hiddenMap.set(id, hide);
  }

  hideAllShapes(hide: boolean) {
    const hiddenMap = this.#tmpState.getMap("hidden_map");
    for (const shapeMap of this.#getShapes()) {
      const shape = this.#shapeToJson(shapeMap);
      hiddenMap.set(shape.id, hide);
    }
  }

  hideAllShapesOfDataType(dataType: DataType, hide: boolean) {
    const hiddenMap = this.#tmpState.getMap("hidden_map");
    for (const shapeMap of this.#getShapes()) {
      const shape = this.#shapeToJson(shapeMap);
      if (shape.dataType === dataType) {
        hiddenMap.set(shape.id, hide);
      }
    }
  }

  editShapeName(id: string, name: string) {
    const shape = this.#getShapeContainer(id);
    if (shape) {
      shape.set("name", name);
    }
  }

  // --- Custom Orders ---

  #getCustomOrders(): LoroMovableList<LoroMap<DocCustomOrder>> {
    return this.#state.getMovableList("custom_orders");
  }

  #getCustomOrderContainer(id: string): LoroMap<DocCustomOrder> | null {
    const orders = this.#getCustomOrders();
    for (let i = 0; i < orders.length; i++) {
      const order = orders.get(i) as LoroMap<DocCustomOrder>;
      if (order.get("id") === id) return order;
    }
    return null;
  }

  getCustomOrders(): Array<{
    id: string;
    name: string;
    description: string | null;
    color: [number, number, number];
    price: number | "TBC";
  }> {
    const orders = this.#getCustomOrders();
    const result: Array<{
      id: string;
      name: string;
      description: string | null;
      color: [number, number, number];
      price: number | "TBC";
    }> = [];
    for (let i = 0; i < orders.length; i++) {
      const order = orders.get(i) as LoroMap<DocCustomOrder>;
      result.push({
        id: order.get("id") as string,
        name: order.get("name") as string,
        description: (order.get("description") as string) ?? null,
        color: order.get("color") as [number, number, number],
        price: (order.get("price") as number | "TBC") ?? "TBC",
      });
    }
    return result;
  }

  getCustomOrder(id: string) {
    const order = this.#getCustomOrderContainer(id);
    if (!order) return null;
    return {
      id: order.get("id") as string,
      name: order.get("name") as string,
      description: (order.get("description") as string) ?? null,
      color: order.get("color") as [number, number, number],
      price: (order.get("price") as number | "TBC") ?? "TBC",
    };
  }

  createCustomOrder(input: {
    name: string;
    description?: string | null;
    color: [number, number, number];
    price?: number | "TBC";
  }): string {
    const id = idLib.shortId();
    const orders = this.#getCustomOrders();
    const order = orders.pushContainer(new LoroMap());
    order.set("id", id);
    order.set("name", input.name);
    if (input.description) {
      order.set("description", input.description);
    }
    order.set("color", input.color);
    if (input.price !== undefined) {
      order.set("price", input.price);
    }
    return id;
  }

  editCustomOrderName(id: string, name: string) {
    const order = this.#getCustomOrderContainer(id);
    if (order) {
      order.set("name", name);
    }
  }

  editCustomOrderDescription(id: string, description: string | null) {
    const order = this.#getCustomOrderContainer(id);
    if (order) {
      if (description) {
        order.set("description", description);
      } else {
        order.delete("description");
      }
    }
  }

  editCustomOrderColor(id: string, color: [number, number, number]) {
    const order = this.#getCustomOrderContainer(id);
    if (order) {
      order.set("color", color);
    }
  }

  deleteCustomOrder(id: string) {
    const orders = this.#getCustomOrders();
    for (let i = 0; i < orders.length; i++) {
      const order = orders.get(i) as LoroMap<DocCustomOrder>;
      if (order.get("id") === id) {
        orders.delete(i, 1);
        // Remove customOrderId from all shapes in this order
        for (const shapeMap of this.#getShapes()) {
          if (shapeMap.get("customOrderId") === id) {
            shapeMap.delete("customOrderId");
          }
        }
        return;
      }
    }
  }

  setShapeCustomOrder(shapeId: string, customOrderId: string | null) {
    const shape = this.#getShapeContainer(shapeId);
    if (shape) {
      if (customOrderId) {
        shape.set("customOrderId", customOrderId);
      } else {
        shape.delete("customOrderId");
      }
    }
  }

  createShape(input: MapShapeInput) {
    const newId = idLib.shortId();
    const shapes = this.#state.getMovableList("shapes");
    const shape = shapes.pushContainer(new LoroMap());

    shape.set("id", newId);
    shape.set("type", input.type);
    shape.set("dataType", input.dataType);
    shape.set("name", input.name);

    if (input.color) {
      shape.set("color", input.color);
    }

    if (input.customOrderId) {
      shape.set("customOrderId", input.customOrderId);
    }

    if (isDerivedShapeInput(input)) {
      invariant(isDocShapeDerived(shape));
      shape.set("baseShapeId", input.baseShapeId);
    }

    if (input.dataType === "_2d_plan_on_map") {
      invariant(isDocShape2dPlanOnMap(shape));
      shape.set("planS3Url", input.planS3Url);
      if (input.assetId) {
        shape.set("assetId", input.assetId);
      }
    }

    if (input.type === "polygon") {
      invariant(isDocShapePolygon(shape));
      const exterior = shape.setContainer("exterior", new LoroMovableList());
      for (const [i, vertex] of input.exterior.entries()) {
        exterior.insert(i, positionLib.toString(vertex));
      }
    } else if (input.type === "rectangle") {
      invariant(isDocShapeRectangle(shape));
      const exterior = shape.setContainer("exterior", new LoroMap());
      exterior.set(Corner.sw, positionLib.toString(input.exterior.sw));
      exterior.set(Corner.se, positionLib.toString(input.exterior.se));
      exterior.set(Corner.ne, positionLib.toString(input.exterior.ne));
      exterior.set(Corner.nw, positionLib.toString(input.exterior.nw));
    } else if (input.type === "path") {
      invariant(isDocShapePath(shape));
      const vertices = shape.setContainer("vertices", new LoroMovableList());
      for (const [i, vertex] of input.vertices.entries()) {
        vertices.insert(i, positionLib.toString(vertex));
      }
    } else if (input.type === "extents") {
      invariant(isDocShapeExtents(shape));
      const extents = shape.setContainer("extents", new LoroMovableList());
      for (const [i, extent] of input.extents.entries()) {
        extents.insert(i, positionLib.toString(extent));
      }
    }

    if (
      (input.type === "polygon" || input.type === "rectangle") &&
      input.holes
    ) {
      invariant(isDocShapePolygon(shape) || isDocShapeRectangle(shape));
      const holes = shape.setContainer("holes", new LoroMovableList());
      for (const [i, hole] of input.holes.entries()) {
        const holeList = holes.insertContainer(i, new LoroMovableList());
        for (const [j, vertex] of hole.entries()) {
          holeList.insert(j, positionLib.toString(vertex));
        }
      }
    }

    return newId;
  }

  duplicateShape(
    id: string,
    opts: {
      newDataType?: DataType;
      newBaseShapeId?: string;
    } = {},
  ) {
    const shape = this.getShape(id);
    if (!shape) return null;
    if (opts.newDataType) {
      shape.dataType = opts.newDataType;
      if (isDerivedShapeInput(shape)) {
        if (!opts.newBaseShapeId) {
          throw new Error(
            "baseShapeId is required if newDataType is a derived data type",
          );
        }
        shape.baseShapeId = opts.newBaseShapeId;
      }
      if (!isCustomOrderDataType(opts.newDataType)) {
        delete shape.customOrderId;
        delete shape.color;
      }
    }
    return this.createShape(shape);
  }

  duplicateMap({
    frontiers,
    blank,
  }: {
    frontiers?: Frontiers;
    blank?: boolean;
  }) {
    const previousFrontiers = this.frontiers();
    if (frontiers && !blank) {
      this.restore(frontiers);
    }
    return {
      previousFrontiers,
    };
  }

  changeDataType(
    id: string,
    dataType: DataType,
    dryRun = false,
  ): {
    newBaseShapeId?: string | null;
    toDelete: string[];
  } {
    const shape = this.#getShapeContainer(id);
    const toDelete: string[] = [];
    if (!shape || shape.get("dataType") === dataType) return { toDelete };
    const shapeDataType = shape.get("dataType");

    if (isDocShapeDerived(shape)) {
      // was derived

      // delete base if no other dependents
      const deleteBase = () => {
        const derived = this.getDerivedShapes(shape.get("baseShapeId"));
        if (derived.length === 1 && derived[0].id === shape.get("id")) {
          toDelete.push(shape.get("baseShapeId"));
        }
      };

      if (isDerivedDataType(dataType)) {
        // new derived
        if (
          getBaseDataType(dataType) === getBaseDataType(shape.get("dataType"))
        ) {
          // same base type
          // just change the type, simplest case
          if (!dryRun) {
            shape.set("dataType", dataType);
          }
          return { toDelete };
        } else {
          deleteBase();
        }
      } else if (!isCustomOrderDataType(dataType)) {
        deleteBase();
      }
    } else {
      if (isDerivedDataType(dataType)) {
        const requiredBaseType = getBaseDataType(dataType);
        // if same base type, duplicate the parent and set the newDataType and newBaseShapeId
        if (shapeDataType === requiredBaseType) {
          this.duplicateShape(id, {
            newDataType: dataType,
            newBaseShapeId: id,
          });
          return {
            newBaseShapeId: id,
            toDelete: [],
          };
        }

        toDelete.push(
          ...this.getDerivedShapes(shape.get("id")).map((s) => s.id),
        );
      } else {
        if (!isBaseDataType(dataType)) {
          toDelete.push(
            ...this.getDerivedShapes(shape.get("id")).map((s) => s.id),
          );
        } else {
          const derivedShapes = [...this.#getShapes()].filter(
            (shapeMap) =>
              isDocShapeDerived(shapeMap) && shapeMap.get("baseShapeId") === id,
          );

          if (derivedShapes.length > 0) {
            // update all derived shapes with the new base type
            for (const derivedShape of derivedShapes) {
              const currentDerivedType = derivedShape.get("dataType");
              const newDerivedDataTypes = getDerivedDataTypes(dataType);
              // Get the prefix (e.g., 'bim' or 'twod_plan') from current type
              // Find the new derived type that matches the prefix
              const prefix = currentDerivedType.split("_")[0];
              const newDerivedType = newDerivedDataTypes.find(
                (derivedType: DerivedDataType) =>
                  derivedType.startsWith(prefix),
              );

              if (newDerivedType) {
                derivedShape.set("dataType", newDerivedType);
              } else {
                // If no matching derived type found for the prefix, add to toDelete array
                toDelete.push(derivedShape.get("id"));
              }
            }
          }
        }
      }
    }

    if (isDerivedDataType(dataType)) {
      // new derived

      // create new base
      let newBaseShapeId: string | null = null;
      if (!dryRun) {
        newBaseShapeId = this.duplicateShape(id, {
          newDataType: getBaseDataType(dataType),
        });
        invariant(newBaseShapeId, `shape with ID ${id} does not exist`);
        shape.set("dataType", dataType);
        invariant(isDocShapeDerived(shape));
        shape.set("baseShapeId", newBaseShapeId);

        this.deleteShapes(...toDelete);
      }

      return { newBaseShapeId, toDelete };
    }

    if (!dryRun) {
      shape.set("dataType", dataType);
      shape.delete("baseShapeId");

      this.deleteShapes(...toDelete);
    }

    return { toDelete };
  }

  getDerivedShapes(id: string) {
    const shapes = this.#state.getMovableList("shapes");
    return shapes
      .toArray()
      .filter((s) => isDocShapeDerived(s) && s.get("baseShapeId") === id)
      .map(this.#shapeToJson.bind(this));
  }

  getChildCounts() {
    const childCounts = new Map<string, number>();
    const shapes = this.getShapes();
    for (const shape of shapes) {
      const derivedShapes = shapes.filter(
        (s) => isMapShapeDerived(s) && s.baseShapeId === shape.id,
      );
      childCounts.set(shape.id, derivedShapes.length);
    }
    return childCounts;
  }

  deleteShapes(...ids: string[]) {
    const shapes = this.#state.getMovableList("shapes");
    const derivedShapes = ids.flatMap((id) => this.getDerivedShapes(id));
    const allIds = [
      ...new Set([
        // ...new Set(
        ...derivedShapes.map((s) => s.id),
        ...ids,
        // ),
      ]),
    ];
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes.get(i);
      if (allIds.includes(shape.get("id"))) {
        shapes.delete(i, 1);
      }
    }
  }

  deleteAllShapes() {
    const shapes = this.#state.getMovableList("shapes");
    for (let i = shapes.length - 1; i >= 0; i--) {
      shapes.delete(i, 1);
    }
  }

  // deleteAllShapesOfDataType(dataType: DataType) {
  //   const shapes = [...this.#getShapes()].filter(
  //     (s) => s.get("dataType") === dataType
  //   );
  //   const derivedIds = shapes.flatMap((s) =>
  //     this.getDerivedShapes(s.get("id")).map((s) => s.get("id"))
  //   );
  //   invariant(derivedIds.length === new Set(derivedIds).size);
  //   this.deleteShapes(...derivedIds, ...shapes.map((s) => s.get("id")));
  // }

  getShapesOfDataType(dataType: DataType) {
    return [...this.#getShapes()]
      .filter((s) => s.get("dataType") === dataType)
      .map(this.#shapeToJson.bind(this));
  }

  getTentativeShape(clientId: string) {
    const tentativeShapes = this.#tmpState.getMap("tentative_shapes");
    return tentativeShapes.get(clientId);
  }

  /**
   * `parentIndex` must be defined if creating a boolean
   */
  createTentativeShape(
    clientId: string,
    type: TentativeShapeType,
    coordinate: Position,
    opts: {
      parentIndex?: number;
    } = {},
  ) {
    const tentativeShapes = this.#tmpState.getMap("tentative_shapes");
    const shape = tentativeShapes.setContainer(clientId, new LoroMap());
    shape.set("type", type);
    shape.set("id", clientId);
    switch (type) {
      case "polygon": {
        invariant(isDocTentativeShapePolygon(shape));
        const exterior = shape.setContainer("exterior", new LoroMovableList());
        exterior.push(positionLib.toString(coordinate));
        break;
      }
      case "rectangle": {
        invariant(isDocTentativeShapeRectangle(shape));
        shape.set("initial", positionLib.toString(coordinate));
        break;
      }
      case "path": {
        invariant(isDocTentativeShapePath(shape));
        const vertices = shape.setContainer("vertices", new LoroMovableList());
        vertices.push(positionLib.toString(coordinate));
        break;
      }
      case "boolean": {
        invariant(isDocTentativeShapeBoolean(shape));
        invariant(
          opts.parentIndex !== undefined,
          "parent index must be defined if creating boolean",
        );
        shape.set("parent_index", opts.parentIndex);
        const parentShape = this.#getShapeContainerByIndex(opts.parentIndex);
        console.log("BOOLEAN PARENT", parentShape);
        invariant(parentShape, "parent shape does not exist");
        invariant(
          parentShape.get("type") !== "path",
          "cannot create boolean in path",
        );
        const vertices = shape.setContainer("vertices", new LoroMovableList());
        vertices.push(positionLib.toString(coordinate));
        break;
      }
    }
  }

  appendTentativeShape(clientId: string, coordinate: Position) {
    const tentativeShape = this.getTentativeShape(clientId);
    if (!tentativeShape) return;
    const type = tentativeShape.get("type");
    switch (type) {
      case "polygon": {
        invariant(isDocTentativeShapePolygon(tentativeShape));
        const exterior = tentativeShape.get("exterior");
        exterior.push(positionLib.toString(coordinate));
        break;
      }
      case "path": {
        invariant(isDocTentativeShapePath(tentativeShape));
        const vertices = tentativeShape.get("vertices");
        vertices.push(positionLib.toString(coordinate));
        break;
      }
      case "boolean": {
        invariant(isDocTentativeShapeBoolean(tentativeShape));
        const vertices = tentativeShape.get("vertices");
        vertices.push(positionLib.toString(coordinate));
        break;
      }
    }
  }

  /**
   * Returns a function that finishes the tentative shape - `coordinate` is required when finishing a rectangle.
   */
  finishTentativeShape(
    clientId: string,
    opts: { name?: string; color?: [number, number, number] } = {},
  ) {
    const tentativeShapes = this.#tmpState.getMap("tentative_shapes");
    const shape = tentativeShapes.get(clientId);
    if (!shape) return;
    const type = shape.get("type");
    switch (type) {
      case "polygon": {
        invariant(isDocTentativeShapePolygon(shape));
        return () => {
          const tentativeExterior = shape.get("exterior");
          if (tentativeExterior.length < 3) return null;
          const newId = this.createShape({
            type: "polygon",
            exterior: tentativeExterior.toArray().map(positionLib.fromString),
            name: opts.name,
            dataType: "unknown",
            color: opts.color,
          });
          tentativeShapes.delete(clientId);
          return newId;
        };
      }
      case "rectangle": {
        invariant(isDocTentativeShapeRectangle(shape));
        return (coordinate?: Position) => {
          invariant(
            coordinate,
            "coordinate is required when finishing rectangle",
          );
          const initial = positionLib.fromString(shape.get("initial"));
          const vertices = oppositeCornersToRectangle(initial, coordinate);
          const newId = this.createShape({
            type: "rectangle",
            exterior: {
              sw: vertices.sw,
              se: vertices.se,
              ne: vertices.ne,
              nw: vertices.nw,
            },
            name: opts.name,
            dataType: "unknown",
            color: opts.color,
          });
          tentativeShapes.delete(clientId);
          return newId;
        };
      }
      case "path": {
        invariant(isDocTentativeShapePath(shape));
        return () => {
          const tentativeVertices = shape.get("vertices");
          if (tentativeVertices.length < 2) return null;
          const newId = this.createShape({
            type: "path",
            vertices: tentativeVertices.toArray().map(positionLib.fromString),
            name: opts.name,
            dataType: "unknown",
            color: opts.color,
          });
          tentativeShapes.delete(clientId);
          return newId;
        };
      }
      case "boolean": {
        invariant(isDocTentativeShapeBoolean(shape));
        return () => {
          const tentativeVertices = shape.get("vertices");
          if (tentativeVertices.length < 3) return null;
          const parentShape = this.#getShapeContainerByIndex(
            shape.get("parent_index"),
          );
          if (!parentShape || parentShape.get("type") === "path") return null;
          const parentHoles = parentShape.getOrCreateContainer(
            "holes",
            new LoroMovableList(),
          );
          const hole = parentHoles.pushContainer(new LoroMovableList());
          for (const vertex of tentativeVertices.toArray()) {
            hole.push(vertex);
          }
          tentativeShapes.delete(clientId);
          return parentShape.get("id");
        };
      }
    }
  }

  deleteTentativeShape(clientId: string) {
    const tentativeShapes = this.#tmpState.getMap("tentative_shapes");
    tentativeShapes.delete(clientId);
  }

  deleteVertex(id: string, index: number) {
    const shape = this.#getShapeContainer(id);
    if (!shape) return;
    switch (shape.get("type")) {
      case "polygon": {
        invariant(isDocShapePolygon(shape));
        const exterior = shape.get("exterior");
        if (exterior.length <= 3) {
          this.deleteShapes(shape.get("id"));
        } else {
          exterior.delete(index, 1);
        }
        break;
      }
      case "path": {
        invariant(isDocShapePath(shape));
        const vertices = shape.get("vertices");
        if (vertices.length <= 2) {
          this.deleteShapes(shape.get("id"));
        } else {
          vertices.delete(index, 1);
        }
        break;
      }
    }
  }

  deleteHoleVertex(id: string, holeIndex: number, vertexIndex: number) {
    const parent = this.#getShapeContainer(id);
    if (!parent) return;
    if (
      parent.get("type") === "polygon" ||
      parent.get("type") === "rectangle"
    ) {
      invariant(isDocShapePolygon(parent) || isDocShapeRectangle(parent));
      const holes = parent.get("holes");
      if (!holes) return;
      const hole = holes.get(holeIndex);
      if (hole.length <= 3) {
        holes.delete(holeIndex, 1);
      } else {
        hole.delete(vertexIndex, 1);
      }
    }
  }

  addVertex(id: string, coordinate: Position, indexBefore: number) {
    const shape = this.#getShapeContainer(id);
    if (!shape) return;

    const newVertexIndex = indexBefore + 1;

    switch (shape.get("type")) {
      case "polygon": {
        invariant(isDocShapePolygon(shape));
        const exterior = shape.get("exterior");
        exterior.insert(newVertexIndex, positionLib.toString(coordinate));
        break;
      }
      case "path": {
        invariant(isDocShapePath(shape));
        const vertices = shape.get("vertices");
        vertices.insert(newVertexIndex, positionLib.toString(coordinate));
        break;
      }
    }
  }

  addHoleVertex(
    id: string,
    coordinate: Position,
    holeIndex: number,
    indexBefore: number,
  ) {
    const shape = this.#getShapeContainer(id);
    if (!shape) return;
    if (!isDocShapePolygon(shape) && !isDocShapeRectangle(shape)) return;
    const holes = shape.get("holes");
    if (!holes) return;
    const hole = holes.get(holeIndex);
    if (!hole) return;
    const newVertexIndex = indexBefore + 1;
    hole.insert(newVertexIndex, positionLib.toString(coordinate));
  }

  startDragging(
    clientId: string,
    opts: {
      shapeIndex: number;
      vertexIndex?: number;
      holeIndex?: number;
      corner?: Corner;
    },
  ) {
    const { shapeIndex, vertexIndex, holeIndex, corner } = opts;
    const shape = this.#getShapeContainerByIndex(opts.shapeIndex);
    if (!shape) return;
    const me = this.#tmpState
      .getMap("clients")
      .getOrCreateContainer<LoroMap<ClientState>>(clientId, new LoroMap());
    if (
      (isDocShapePolygon(shape) || isDocShapeRectangle(shape)) &&
      holeIndex !== undefined
    ) {
      const holes = shape.get("holes");
      if (!holes) return;
      const hole = holes.get(holeIndex);
      if (!hole) return;
      if (vertexIndex === undefined) return;
      me.set("drag_shape_index", shapeIndex);
      me.set("drag_hole_index", holeIndex);
      me.set("drag_vertex_index", vertexIndex);
    } else if (isDocShapePolygon(shape)) {
      if (vertexIndex === undefined) return;
      me.set("drag_shape_index", shapeIndex);
      me.set("drag_vertex_index", vertexIndex);
    } else if (isDocShapeRectangle(shape)) {
      if (corner === undefined) return;
      me.set("drag_shape_index", shapeIndex);
      me.set("drag_corner", corner);
    } else if (isDocShapePath(shape)) {
      if (vertexIndex === undefined) return;
      me.set("drag_shape_index", shapeIndex);
      me.set("drag_vertex_index", vertexIndex);
    }
  }

  stopDragging(clientId: string) {
    const me = this.#tmpState.getMap("clients").get(clientId);
    if (!me) return;
    me.delete("drag_shape_index");
    me.delete("drag_vertex_index");
    me.delete("drag_hole_index");
    me.delete("drag_corner");
  }

  updatePolygonVertexPosition(
    shapeIndex: number,
    vertexIndex: number,
    coordinate: Position,
  ) {
    const shape = this.#getShapeContainerByIndex(shapeIndex);
    if (!shape || !isDocShapePolygon(shape)) return;
    const exterior = shape.get("exterior");
    exterior.set(vertexIndex, positionLib.toString(coordinate));
  }

  updateRectangleVertexPosition(
    shapeIndex: number,
    corner: Corner,
    coordinate: Position,
  ) {
    const shape = this.#getShapeContainerByIndex(shapeIndex);
    if (!shape || !isDocShapeRectangle(shape)) return;
    const exterior = shape.get("exterior");
    const [cornerH, cornerV] = rectangleLib.getAdjacentCorners(corner);
    const vertexH = positionSchema.parse(exterior.get(cornerH));
    const vertexV = positionSchema.parse(exterior.get(cornerV));
    exterior.set(corner, positionLib.toString(coordinate));
    exterior.set(cornerH, positionLib.toString([vertexH[0], coordinate[1]]));
    exterior.set(cornerV, positionLib.toString([coordinate[0], vertexV[1]]));
  }

  updatePathVertexPosition(
    shapeIndex: number,
    vertexIndex: number,
    coordinate: Position,
  ) {
    const shape = this.#getShapeContainerByIndex(shapeIndex);
    if (!shape || !isDocShapePath(shape)) return;
    const vertices = shape.get("vertices");
    vertices.set(vertexIndex, positionLib.toString(coordinate));
  }

  updateBooleanVertexPosition(
    shapeIndex: number,
    holeIndex: number,
    vertexIndex: number,
    coordinate: Position,
  ) {
    const shape = this.#getShapeContainerByIndex(shapeIndex);
    if (!shape || (!isDocShapePolygon(shape) && !isDocShapeRectangle(shape)))
      return;
    const holes = shape.get("holes");
    if (!holes) return;
    const hole = holes.get(holeIndex);
    if (!hole) return;
    hole.set(vertexIndex, positionLib.toString(coordinate));
  }

  initializeClient(
    clientId: string,
    opts: { name?: string | null; image?: string | null },
  ) {
    const clients = this.#tmpState.getMap("clients");
    const sizeBefore = clients.size;
    const me = clients.getOrCreateContainer<LoroMap<ClientState>>(
      clientId,
      new LoroMap(),
    );
    const settings = me.getOrCreateContainer<LoroMap<UserSettings>>(
      "user_settings",
      new LoroMap(),
    );
    settings.set("id", clientId);
    if (opts.name) settings.set("name", opts.name);
    if (opts.image) settings.set("image", opts.image);
    settings.set("color_index", sizeBefore);
  }

  updateSetting<T extends keyof UserSettings>(
    clientId: string,
    key: T,
    value: UserSettings[T],
  ) {
    const me = this.#tmpState
      .getMap("clients")
      .getOrCreateContainer<LoroMap<ClientState>>(clientId, new LoroMap());
    const settings = me.getOrCreateContainer<LoroMap<UserSettings>>(
      "user_settings",
      new LoroMap(),
    );
    if (!value) return;
    settings.set(key, value);
  }

  dropClient(clientId: string) {
    this.#tmpState.getMap("clients").delete(clientId);
  }

  contribute(contributorId: number, timestamp: number) {
    const contributors = this.#state.getMap("contributors");
    contributors.set(`${contributorId}`, timestamp);
  }

  createOffsetShape(
    id: string,
    distanceMeters: number = 10,
    opts: {
      name?: string;
      dataType?: PointCloudDataType | "unknown" | undefined;
    } = {
      dataType: "exterior",
    },
  ) {
    const original = this.getShape(id);
    if (!original) return null;

    if (original.type === "path") {
      console.log("Offset shape not supported for path");
      return null;
    }

    if (original.type === "extents") {
      console.log("Offset shape not supported for extents");
      return null;
    }

    // Convert rectangle to polygon points if needed
    const exteriorPoints: Position[] =
      original.type === "rectangle"
        ? ([
            original.exterior.sw,
            original.exterior.se,
            original.exterior.ne,
            original.exterior.nw,
          ] as Position[])
        : original.exterior;

    // Work fully in Web-Mercator metres; no earth-curvature constants.
    type Point2D = { x: number; y: number };
    const mercatorPoints: Point2D[] = exteriorPoints.map(
      (position: Position) => {
        const [x, y] = turf.toMercator(position) as Position;
        return { x, y };
      },
    );

    // determine if vertices are in counter-clockwise order
    // shoelace formula = twice the area of the polygon to determine signed area
    const doubleSignedArea = mercatorPoints.reduce(
      (accumulator, currentPoint, index) => {
        const nextPoint = mercatorPoints[(index + 1) % mercatorPoints.length];
        return (
          accumulator +
          currentPoint.x * nextPoint.y -
          nextPoint.x * currentPoint.y
        );
      },
      0,
    );

    // clockwise if doubleSignedArea > 0, otherwise counter-clockwise
    const isCounterClockwise = doubleSignedArea > 0;

    // delta distance calculation
    const latRadPoly = (exteriorPoints[0][1] * Math.PI) / 180;
    const scaleFactorPoly = 1 / Math.cos(latRadPoly);
    const delta = distanceMeters * scaleFactorPoly;

    // Creates a parallel line that's moved outward by the delta distance
    const offsetLines: { startPoint: Point2D; endPoint: Point2D }[] = [];
    for (let i = 0; i < mercatorPoints.length; i++) {
      const currentPoint = mercatorPoints[i];
      const nextPoint = mercatorPoints[(i + 1) % mercatorPoints.length];

      // Calculate edge vector
      const edgeX = nextPoint.x - currentPoint.x;
      const edgeY = nextPoint.y - currentPoint.y;
      const edgeLength = Math.hypot(edgeX, edgeY);

      if (edgeLength === 0) continue;

      // Calculate normal vector (perpendicular directions) - figure out which way to push each line
      const normalX = (isCounterClockwise ? edgeY : -edgeY) / edgeLength;
      const normalY = (isCounterClockwise ? -edgeX : edgeX) / edgeLength;

      // Create offset line parallel to the edge
      offsetLines.push({
        startPoint: {
          x: currentPoint.x + normalX * delta,
          y: currentPoint.y + normalY * delta,
        },
        endPoint: {
          x: nextPoint.x + normalX * delta,
          y: nextPoint.y + normalY * delta,
        },
      });
    }

    /**
     * Uses determinants to calculate the exact crossing point
     * Returns null if lines are parallel.
     */
    const findLineIntersection = (
      line1Start: Point2D,
      line1End: Point2D,
      line2Start: Point2D,
      line2End: Point2D,
    ): Point2D | null => {
      const dx1 = line1End.x - line1Start.x;
      const dy1 = line1End.y - line1Start.y;
      const dx2 = line2End.x - line2Start.x;
      const dy2 = line2End.y - line2Start.y;

      const determinant = dx1 * dy2 - dy1 * dx2;
      if (Math.abs(determinant) < 1e-10) return null; // parallel lines

      const t1 =
        ((line2Start.x - line1Start.x) * dy2 -
          (line2Start.y - line1Start.y) * dx2) /
        determinant;

      return {
        x: line1Start.x + t1 * dx1,
        y: line1Start.y + t1 * dy1,
      };
    };

    /**
     * Takes all parallel lines then find where each pair neighboring lines cross.
     * Intersection points are the vertices of the offset polygon.
     * If lines are parallel, use the endpoint of current line.
     */
    const offsetPolygonPoints: Point2D[] = [];
    for (let i = 0; i < offsetLines.length; i++) {
      const currentLine = offsetLines[i];
      const nextLine = offsetLines[(i + 1) % offsetLines.length];

      const intersection = findLineIntersection(
        currentLine.startPoint,
        currentLine.endPoint,
        nextLine.startPoint,
        nextLine.endPoint,
      );

      if (intersection) {
        offsetPolygonPoints.push(intersection);
      } else {
        // use current line
        offsetPolygonPoints.push(currentLine.endPoint);
      }
    }

    // Close the polygon, connect back to start point
    if (offsetPolygonPoints.length > 0) {
      offsetPolygonPoints.push(offsetPolygonPoints[0]);
    }

    // converts back to WGS84 coordinates (lat/lng)
    const newExterior: Position[] = offsetPolygonPoints.map(
      ({ x, y }) => turf.toWgs84([x, y]) as Position,
    );

    const newHoles: Position[][] = [exteriorPoints];

    return this.createShape({
      type: "polygon",
      dataType: opts.dataType ?? "exterior",
      name: opts.name ?? original.name ?? null,
      exterior: newExterior,
      holes: newHoles,
    });
  }

  editShapeColor(id: string, color: [number, number, number] | null) {
    const shape = this.#getShapeContainer(id);
    if (shape) {
      if (color) {
        shape.set("color", color);
      } else {
        shape.delete("color");
      }
    }
  }
}

export function isDocShapePolygon(
  map: LoroMap<DocShape>,
): map is LoroMap<DocPolygon> {
  return map.get("type") === "polygon";
}
export function isDocShapeRectangle(
  map: LoroMap<DocShape>,
): map is LoroMap<DocRectangle> {
  return map.get("type") === "rectangle";
}
export function isDocShapePath(
  map: LoroMap<DocShape>,
): map is LoroMap<DocPath> {
  return map.get("type") === "path";
}
export function isDocShapeExtents(
  map: LoroMap<DocShape>,
): map is LoroMap<DocExtents> {
  return map.get("type") === "extents";
}
export function isDocShapeDerived(
  map: LoroMap<DocShape>,
): map is LoroMap<DocDerivedShape> {
  return isDerivedDataType(map.get("dataType"));
}
export function isDocShape2dPlanOnMap(
  map: LoroMap<DocShape>,
): map is LoroMap<Doc2dPlanOnMapShape> {
  return map.get("dataType") === "_2d_plan_on_map";
}

export function isDocTentativeShapePolygon(
  map: LoroMap<DocTentativeShape>,
): map is LoroMap<DocTentativePolygon> {
  return map.get("type") === "polygon";
}
export function isDocTentativeShapeRectangle(
  map: LoroMap<DocTentativeShape>,
): map is LoroMap<DocTentativeRectangle> {
  return map.get("type") === "rectangle";
}
export function isDocTentativeShapePath(
  map: LoroMap<DocTentativeShape>,
): map is LoroMap<DocTentativePath> {
  return map.get("type") === "path";
}
export function isDocTentativeShapeBoolean(
  map: LoroMap<DocTentativeShape>,
): map is LoroMap<DocTentativeBoolean> {
  return map.get("type") === "boolean";
}

export function isMapShapeDerived(shape: MapShape): shape is DerivedMapShape {
  return isDerivedDataType(shape.dataType);
}
